import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { PUR2040_R01Dto, defaultPUR2040_R01Dto, GridRowData } from "./Dto_PUR2040_R01";
import { usePUR2040_R01Store } from "./Store_PUR2040_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { formatNumber } from "src/utils/number/numberUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR2040_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<GridRowData> | null>;
}

export const Table_PUR2040_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        PUR2040_R01List,
        selectedPUR2040_R01List, addSelectedPUR2040_R01List, removeSelectedPUR2040_R01List, initSelectedPUR2040_R01List,
        columnState, setColumnState,
        scrollTop, setScrollTop,
    } = usePUR2040_R01Store();

    const { 
        unitList,
        compCdList,
    } = useCommonCodeStore();

    const codeMaps = useMemo(() => {
        return {
            unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
            comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
        };
    }, [unitList, compCdList]);

    const {
        getRowId,
        createRowClassRules,
        paddedGridData,
        settingRowIndex,
        ensureSelectedRow,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: PUR2040_R01List || [],
        defaultGridData: defaultPUR2040_R01Dto as GridRowData,
        selectedRow: selectedPUR2040_R01List,
    });

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        compNm: '사업장',
        purchaseDt: '날짜',
        customCd: '거래처',
        customNm: '상호',
        itemCd: '품목코드',
        itemNm: '품명',
        itemSpec: '규격',
        locationNm: '보관위치',
        billQty: '수량',
        billUnit: '단위',
        unitNm: '단위명',
        billPrice: '단가',
        dcAmt: '할인액',
        billAmt: '매입금액',
        remark: '비고',
    };

    const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

    const baseColumnDefs: ColDef<PUR2040_R01Dto>[] = useMemo(() => [
            ...getDefaultRowIndexColumnConfig(),
            {
                headerName: headerNames.compNm, colId: 'compNm', field: 'compNm', width: 120,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'compNm'),
            },
            {
                headerName: headerNames.purchaseDt, colId: 'purchaseDt', field: 'purchaseDt', width: 150,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'purchaseDt'),
                valueFormatter: (p: { data?: PUR2040_R01Dto }) => {
                    if (p.data?.purchaseDt) {
                        const date = new Date(p.data.purchaseDt);
                        if (!isNaN(date.getTime())) return formatDate(date);
                    }
                    return '';
                },
                comparator: cellDateComparator,
            },
            {
                headerName: headerNames.customCd, colId: 'customCd', field: 'customCd', width: 110,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'customCd'),
            },
            {
                headerName: headerNames.customNm, colId: 'customNm', field: 'customNm', width: 140,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'customNm'),
            },
            {
                headerName: headerNames.itemCd, colId: 'itemCd', field: 'itemCd', width: 110,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'itemCd'),
            },
            {
                headerName: headerNames.itemNm, colId: 'itemNm', field: 'itemNm', width: 200,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'itemNm'),
            },
            {
                headerName: headerNames.itemSpec, colId: 'itemSpec', field: 'itemSpec', width: 200,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'itemSpec'),
            },
            {
                headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'locationNm'),
            },
            {
                headerName: headerNames.billQty, colId: 'billQty', field: 'billQty', width: 90,
                editable: false,
                type: 'numericColumn',
                headerClass: aggridHeaderClass,
                cellStyle: (p) => getCellStyle(p, 'billQty'),
                valueFormatter: (p) => formatNumber(p.value || 0),
            },
            {
                headerName: headerNames.billUnit, colId: 'billUnit', field: 'billUnit', width: 80,
                cellStyle: (p) => getCellStyle(p, 'billUnit'),
                ...CommonCodeColumn.options(unitList, false, undefined, undefined),
            },
            {
                headerName: headerNames.unitNm, colId: 'unitNm', field: 'unitNm', width: 80,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'unitNm'),
            },
            {
                headerName: headerNames.billPrice, colId: 'billPrice', field: 'billPrice', width: 100,
                editable: false,
                type: 'numericColumn',
                headerClass: aggridHeaderClass,
                cellStyle: (p) => getCellStyle(p, 'billPrice'),
                valueFormatter: (p) => formatNumber(p.value || 0),
            },
            {
                headerName: headerNames.dcAmt, colId: 'dcAmt', field: 'dcAmt', width: 100,
                editable: false,
                type: 'numericColumn',
                headerClass: aggridHeaderClass,
                cellStyle: (p) => getCellStyle(p, 'dcAmt'),
                valueFormatter: (p) => formatNumber(p.value || 0),
            },
            {
                headerName: headerNames.billAmt, colId: 'billAmt', field: 'billAmt', width: 120,
                editable: false,
                type: 'numericColumn',
                headerClass: aggridHeaderClass,
                cellStyle: (p) => getCellStyle(p, 'billAmt'),
                valueFormatter: (p) => formatNumber(p.value || 0),
            },
            {
                headerName: headerNames.remark, colId: 'remark', field: 'remark', width: 200,
                editable: false,
                cellStyle: (p) => getCellStyle(p, 'remark'),
            },
    ], [getCellStyle, cellDateComparator, unitList, headerNames]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs: ColDef<GridRowData>[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<GridRowData>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


    const shiftRangeSelectionConfig = useMemo(() => ({
        enableShiftRangeSelect: true,
        idKey: 'valueIndex' as const,
        selectedIds: selectedPUR2040_R01List as Array<string | number>,
        setSelectedIds: (ids: Array<string | number>) => {
            removeSelectedPUR2040_R01List(selectedPUR2040_R01List);
            addSelectedPUR2040_R01List(ids.map(String));
        },
    }), [selectedPUR2040_R01List, addSelectedPUR2040_R01List, removeSelectedPUR2040_R01List]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = PUR2040_R01List;
        const totals = detailRows.reduce((acc, item) => {
            return {
                billQty: acc.billQty + (Number(item.billQty) || 0),
                billAmt: acc.billAmt + (Number(item.billAmt) || 0),
                editable: false,
            };
        }, { billQty: 0, billAmt: 0, editable: false });

        return [{
            rowIndex: '',
            purchaseDt: detailRows.length.toLocaleString() + "건",
            billQty: totals.billQty,
            billAmt: totals.billAmt,
            editable: false,
        }];
    }, [PUR2040_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [PUR2040_R01List, selectedPUR2040_R01List]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: GridRowData[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as GridRowData);
        });
    }, [gridRef]);

    return (
        <GridContextWrapper<GridRowData>
        	enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedPUR2040_R01List}
            defaultRowData={defaultPUR2040_R01Dto}
            baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Touch<GridRowData>
                ref={gridRef}
                rowData={paddedGridData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                onCellKeyDown={undefined}
                rowClassRules={
                    createRowClassRules<GridRowData>({
                        idKey: 'valueIndex',
                        selectedList: selectedPUR2040_R01List,
                        deletedList: [],
                    })
                }
                shiftRangeSelection={shiftRangeSelectionConfig}
                pinnedBottomRowData={pinnedBottomRow}
                setHoveredRowIndex={setHoveredRowIndex}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                rowDragManaged={true}
                rowDragEntireRow={true}
                onRowDragEnd={handleRowDragEnd}
                isRowSelectable={(node) => !!node.data}
                suppressRowClickSelection={true}
                ensureDomOrder={true}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
}
